import React from 'react';

import {
  useTimers,
  useTimerEvents,
  useTimerActions
} from '../../hooks/use-game-timer';
import {
  NotificationContext,
  NotificationContent
} from '../../contexts/notification';
import { GameContext } from '../../contexts/game';
import { useTimerContext } from '../../contexts/timer';
import { OverlayContext } from '../../contexts/overlay';
import { useGameEvent } from '../../hooks/use-game-event';
import { GameDocumentContext } from '../../contexts/game-document';
import { GetTimerObjectById } from '../../utils/game-document/assets';
import { GetResourceValue } from '../../utils/game-document/resources';

/**
 * these components is handling all timer events (onStart, onStop, and onElapsed)
 * handling for displaying the 10 seconds remaining notification
 * handling for game status
 */

export const TimersGameSync = () => {
  const [game] = React.useContext(GameContext);
  const { pauseAll, resumeAll, stopAll } = useTimerActions();

  const status = game.gameState?.status;
  const prevStatus = React.useRef<typeof status>(undefined);

  React.useEffect(() => {
    const prev = prevStatus.current;
    prevStatus.current = status;

    if (status === 'Paused') pauseAll();
    else if (status === 'Running' && prev === 'Paused') resumeAll();
    else if (status === 'Finished') stopAll();
  }, [status, pauseAll, resumeAll, stopAll]);

  return null;
};

const WARNING_SECONDS = 10;
export const TimerEvents = React.memo(({ id }: { id: string }) => {
  const store = useTimerContext();
  const [state] = React.useContext(GameDocumentContext);
  const [, setOverlay] = React.useContext(OverlayContext);
  const [notification, setNotification] = React.useContext(NotificationContext);
  const { executeTimerEvent } = useGameEvent();

  const showToastNotif = () => {
    const timerDoc = GetTimerObjectById(state.gameDocument!, id);
    const timerTitle =
      timerDoc && timerDoc.titleResId
        ? GetResourceValue(state.gameDocument!, timerDoc.titleResId, '')
        : 'Response timer';

    const timerNotification: NotificationContent = {
      icon: 'notifications',
      isHide: false,
      message: `${timerTitle} - ${WARNING_SECONDS} seconds remaining`
    };

    setNotification({
      ...notification,
      content: [...notification.content, timerNotification]
    });
  };

  const displayNextAfter = (endedId: string) => {
    const all = store.getTimers();
    const from = all.findIndex((t) => t.id === endedId) + 1;
    const next = [...all.slice(from), ...all.slice(0, from)].find(
      (t) => t.status === 'running'
    );

    setOverlay((prev) =>
      prev.timerAssId === endedId
        ? { ...prev, timerAssId: next?.id ?? '' }
        : prev
    );
  };

  useTimerEvents(id, {
    onStart: (t) => {
      setOverlay((prev) => ({ ...prev, timerAssId: t.id }));
      executeTimerEvent(t.id, 'onStartTimer');
    },
    onStop: (t) => {
      if (t.status === 'paused') executeTimerEvent(t.id, 'onStopTimer');
      if (t.status === 'idle') displayNextAfter(t.id);
    },
    onElapsed: (t) => {
      displayNextAfter(t.id);
      executeTimerEvent(t.id, 'onElapsedTimer');
    }
  });

  const inWarning = React.useSyncExternalStore(
    store.subscribe,
    () => {
      const t = store.getTimer(id);
      if (!t || (t.status !== 'running' && t.status !== 'paused')) return false;
      if (t.durationMs <= WARNING_SECONDS * 1000) return false; // too short, or no limit

      const secs = store.getSeconds(id);
      const remaining =
        t.mode === 'down' ? secs : Math.ceil(t.durationMs / 1000) - secs;
      return remaining <= WARNING_SECONDS;
    },
    () => false
  );

  const armed = React.useRef(!inWarning);
  React.useEffect(() => {
    if (!inWarning) {
      armed.current = true;
      return;
    }
    if (armed.current) {
      armed.current = false;
      showToastNotif();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inWarning]);

  return null;
});

export const AllTimerEvents = () => {
  const { timers } = useTimers();
  return (
    <>
      <TimersGameSync />
      {timers.map((t) => (
        <TimerEvents key={t.id} id={t.id} />
      ))}
    </>
  );
};
